import { callGetApi } from "@/utils/apiWrapper";
import ReportAcConstant from "@/mixins/report/const/reportAcConstant";
import { fnBuildValuationView } from "@/utils/reportValuationCase";

// NZSC-3330: 리포트 적정 매매가·적정 전세가 모듈 데이터.
//
// 오픈 게이트(service_config REPORT_VALUATION_MODULE, CLOSED/TESTER/OPEN)는 **서버가 판정**한다.
//   - TESTER 스위치 + 비테스터  → 서버가 gate:'CLOSED', module:null 로 응답
//   - 데이터 없음 / 산정 보류(ABSTAINED) / 미지원 유형 → module:null
// 따라서 프론트는 **module 유무만** 보고 모듈·목차 항목을 통째로 감춘다(레이아웃 흔적도 남기지 않는다).
// gate 값으로 별도 UI 분기를 만들지 않는다.
//
// 조회 실패/미조회는 fail-safe = 미노출. 게이트 기본값이 CLOSED 이므로 "안 보이는 쪽"이 안전하다.
// 이 파일이 valuation API 를 만지는 유일한 지점이다. BE 계약이 바뀌면
// 여기(호출)와 utils/reportValuationCase.js(정규화)만 고치면 화면은 손대지 않는다.
//
// 프리페치는 리포트 상세 뷰 fnInit()에서 1회. 화면 분기는 동기 getter 로만 읽는다(qna-funnel 1호 패턴).
// 스위치 원값(valuation-status, 무인증)을 먼저 보고 CLOSED 면 리포트별 인증 호출 자체를 생략한다.
const CLIENT_CACHE_MS = 60 * 1000;

const VALID_STATUSES = ["OPEN", "TESTER", "CLOSED"];

export const state = {
  _view: null, // 정규화된 뷰모델 | null(미노출)
  _reportSeq: null, // 현재 캐시된 리포트 식별자
  _fetchedAt: 0,
  _loaded: false, // 1회라도 조회를 끝냈는지(로딩 중 깜빡임 방지용)
  _status: null, // 스위치 원값 'OPEN'|'TESTER'|'CLOSED'|null(미조회)
  _statusFetchedAt: 0,
};

export const mutations = {
  SET_REPORT_VALUATION(state, { reportSeq, view }) {
    state._view = view;
    state._reportSeq = reportSeq;
    state._fetchedAt = Date.now();
    state._loaded = true;
  },
  // 다른 리포트로 이동했을 때 이전 리포트 뷰를 즉시 버린다.
  // (_loaded=false, _fetchedAt=0 이라 캐시 히트로 오인되지 않는다)
  RESET_REPORT_VALUATION(state, reportSeq) {
    state._view = null;
    state._reportSeq = reportSeq;
    state._fetchedAt = 0;
    state._loaded = false;
  },
  SET_REPORT_VALUATION_STATUS(state, status) {
    state._status = status;
    state._statusFetchedAt = Date.now();
  },
};

export const actions = {
  // 오픈 스위치 원값(무인증). 유저별 판정은 없다 — CLOSED 일 때 리포트별 호출을 아끼는 용도.
  async acFetchReportValuationStatus({ state, commit }) {
    if (state._status && Date.now() - state._statusFetchedAt < CLIENT_CACHE_MS) {
      return state._status;
    }
    let status = "CLOSED"; // fail-safe
    try {
      const data = await callGetApi({ url: ReportAcConstant.GET_REPORT_VALUATION_STATUS });
      if (data && VALID_STATUSES.includes(data.status)) status = data.status;
    } catch (error) {
      status = "CLOSED";
    }
    commit("SET_REPORT_VALUATION_STATUS", status);
    return status;
  },

  // 리포트 상세 진입(fnInit)에서 프리페치. 같은 리포트를 60초 안에 다시 물으면 재호출하지 않는다.
  async acFetchReportValuation({ state, commit, dispatch }, reportSeq) {
    if (!reportSeq) {
      commit("RESET_REPORT_VALUATION", null);
      return null;
    }
    if (
      state._loaded &&
      state._reportSeq === reportSeq &&
      Date.now() - state._fetchedAt < CLIENT_CACHE_MS
    ) {
      return state._view;
    }

    // SPA 로 리포트 A→B 이동 시, B 응답이 오기 전까지 A 의 모듈이 그대로 그려지면 안 된다.
    if (state._reportSeq !== reportSeq) commit("RESET_REPORT_VALUATION", reportSeq);

    const status = await dispatch("acFetchReportValuationStatus");
    if (status === "CLOSED") {
      if (state._reportSeq !== reportSeq) return null;
      commit("SET_REPORT_VALUATION", { reportSeq, view: null });
      return null;
    }

    let view = null;
    try {
      const data = await callGetApi({
        url: `${ReportAcConstant.GET_REPORT_VALUATION}/${encodeURIComponent(reportSeq)}/valuation`,
      });
      view = fnBuildValuationView(data);
    } catch (error) {
      view = null; // fail-safe: 미노출 (403/7502 미인증 포함)
    }
    // 늦게 도착한 이전 리포트 응답이 현재 리포트 뷰를 덮어쓰지 않도록 한다
    if (state._reportSeq !== reportSeq) return view;
    commit("SET_REPORT_VALUATION", { reportSeq, view });
    return view;
  },

  // NZSC-3397 후속: 관리자 미리보기(코드 토큰 인증) 전용 조회.
  // 판정은 서버가 소유자 기준으로 한다(스위치·소유자 테스터 여부·점수 컷) —
  // 미리보기 화면 = 소유자가 실제로 보게 될 화면 1:1 파리티.
  // 스위치 상태 선조회 생략: 서버 판정 결과(module null 여부)만 믿는다.
  async acFetchReportValuationPreview({ state, commit }, { code, reportSeq }) {
    if (!code || !reportSeq) {
      commit("RESET_REPORT_VALUATION", reportSeq ?? null);
      return null;
    }
    commit("RESET_REPORT_VALUATION", reportSeq);
    let view = null;
    try {
      const data = await callGetApi({
        url: ReportAcConstant.GET_REPORT_VALUATION_MANAGER_PREVIEW,
        headers: { "NZSC-P-ENC": code },
        // 프리뷰 토큰 401 은 고객 세션과 무관하다 — 만료 링크를 로그인 상태로 열었을 때
        // 공용 래퍼가 고객 access token 을 지우지 않도록 정리를 끈다.
        skipUnauthorizedCleanup: true,
      });
      view = fnBuildValuationView(data);
    } catch (error) {
      view = null; // fail-safe: 미노출
    }
    // 늦게 도착한 응답이 다른 리포트 뷰를 덮어쓰지 않도록(고객 액션과 동일 방어)
    if (state._reportSeq !== reportSeq) return view;
    commit("SET_REPORT_VALUATION", { reportSeq, view });
    return view;
  },
};

export const getters = {
  getReportValuationView(state) {
    return state._view;
  },
  // 목차 항목 · 모듈 노출을 함께 결정하는 단일 판정 지점
  hasReportValuationModule(state) {
    return state._view !== null;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
